'use client';

import clsx from 'clsx';
import Image from 'next/image';
import { memo, useRef, useState, useLayoutEffect, useCallback, useEffect } from 'react';
import AnimatedLink from '@/components/animated-link';
import Cta from "@/components/cta/cta";
import ArrowRight from "@/components/icon/arrow-right";
import { STORIES_BASE_PATH } from "@/constants/routes";
import useBatchCardAnimation from '@/hooks/useBatchCardAnimation';
import useImageLoad from '@/hooks/useImageLoad';


export default memo(function StoryCard({ item, isParentInView, cardIndexWithinBatch, isFirstInBatch, batchIndex, onBatchEnter, isHidden }) {
  function stripHtml(html) {
    return html.replace(/<[^>]*>/g, '');
  }

  const { rootRef, scrollRef } = useBatchCardAnimation({
    index: cardIndexWithinBatch,
    isParentInView,
    isFirstInBatch,
    batchIndex,
    onBatchEnter,
  });

  const overlayRef = useRef(null);
  const excerptRef = useRef(null);

  const escapedExcerpt = stripHtml(item.excerpt);
  const [excerpt, setExcerpt] = useState(() => {
    const capped = escapedExcerpt.slice(0, 85);
    return capped.length < escapedExcerpt.length ? `${capped}…` : capped;
  });

  const truncateExcerpt = useCallback(() => {
    const overlay = overlayRef.current;
    const excerptEl = excerptRef.current;
    if (!overlay || !excerptEl) return;

    const style = getComputedStyle(overlay);
    const availableHeight = overlay.clientHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom);
    const container = excerptEl.parentElement;

    // Reset to max length first to re-measure
    const capped = escapedExcerpt.slice(0, 85);
    excerptEl.textContent = capped.length < escapedExcerpt.length
      ? `"${capped}…"`
      : `"${capped}"`;

    if (container.scrollHeight <= availableHeight) {
      setExcerpt(capped.length < escapedExcerpt.length ? `${capped}…` : capped);
      return;
    }

    let low = 0;
    let high = Math.min(escapedExcerpt.length, 85);
    let bestFit = 0;

    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      excerptEl.textContent = mid < escapedExcerpt.length
        ? `"${escapedExcerpt.slice(0, mid)}…"`
        : `"${escapedExcerpt}"`;

      if (container.scrollHeight <= availableHeight) {
        bestFit = mid;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }

    const finalText = bestFit < escapedExcerpt.length
      ? `${escapedExcerpt.slice(0, bestFit)}…`
      : escapedExcerpt;

    excerptEl.textContent = `"${finalText}"`;
    setExcerpt(finalText);
  }, [escapedExcerpt]);

  const {imageRef, onLoad} = useImageLoad();

  useLayoutEffect(() => {
    truncateExcerpt();
  }, [truncateExcerpt]);

  useEffect(() => {
    const overlay = overlayRef.current;
    if (!overlay) return;

    const ro = new ResizeObserver(truncateExcerpt);
    ro.observe(overlay);
    return () => ro.disconnect();
  }, [truncateExcerpt]);

  return (
    <div ref={scrollRef} className={clsx(isHidden && 'invisible')}>
      <AnimatedLink
        ref={rootRef}
        href={`${STORIES_BASE_PATH}/250-stories/${item.slug}`}
        transitionName="overlay-in"
        className="group relative block opacity-[0.001] will-change-transform motion-reduce:opacity-100 motion-reduce:translate-y-0"
      >
        <Image unoptimized={true} {...item.image} className='opacity-0' ref={imageRef} onLoad={onLoad}/>
        <div ref={overlayRef} className="absolute inset-0 bg-black/70 opacity-0 px-[7%] pt-[7%] pb-[max(7%,20px)] transition-opacity duration-600 group-hover:opacity-100 group-focus-visible:opacity-100 lg:px-[10%] lg:pt-[10%] lg:pb-[max(10%,20px)]">
          <div className="flex flex-col gap-y-6 max-h-[calc(100%-16px)] translate-y-3 opacity-0 transition-all duration-600 ease-[var(--custom-ease-out)] group-hover:translate-y-0 group-hover:opacity-100">
            <p ref={excerptRef} className="body-1-small lg:body-2-small! xl:heading-7!">"{excerpt}"</p>
            <div className="body-3-sans-small xl:body-3-sans">{item.name}</div>
          </div>

          <Cta className="absolute right-[7%] bottom-[7%] cta-light cta-icon-stroke-theme cta-circle-small !w-8 !h-8 opacity-0 transition-all duration-600 ease-(--custom-ease-out) group-hover:opacity-100 group-focus-visible:opacity-100 lg:right-4 lg:bottom-4 xl:right-10 xl:bottom-6" aria-hidden={true}>
            <ArrowRight className="!w-3" />
          </Cta>
        </div>
      </AnimatedLink>
    </div>
  );
});
